import React, { useMemo, useState } from "react";
import {
  Table,
  Tag,
  Skeleton,
  Avatar,
  Input,
  Dropdown,
} from "antd";
import moment from "moment";
import { Link, useNavigate } from "react-router-dom";
import "./OrdersTable.css";

import {
    SyncOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    ToolOutlined,
    EyeOutlined,
    SolutionOutlined,
    CarOutlined,
    QuestionCircleOutlined,
    StopOutlined,
    ExclamationCircleOutlined,
    UserOutlined,
    SearchOutlined,
    FilterOutlined
} from '@ant-design/icons';

const statusDisplayMap = {
    1: { text: "En Revisión", color: "#d3adf7", icon: <SolutionOutlined /> },
    2: { text: "En Cotización", color: "#FFD700", icon: <CheckCircleOutlined /> },
    3: { text: "Pendiente Aprob", color: "#d9d9d9", icon: <ClockCircleOutlined /> },
    4: { text: "Construcción", color: "#69c0ff", icon: <ToolOutlined /> },
    5: { text: "Inspección", color: "#87e8de", icon: <EyeOutlined /> },
    6: { text: "En Camino", color: "#95de64", icon: <CarOutlined /> },
    7: { text: "Entregado", color: "#b7eb8f", icon: <CheckCircleOutlined /> },
    8: { text: "Rechazado", color: "#ff7875", icon: <CloseCircleOutlined /> },
    9: { text: "Cancelado", color: "#ff7875", icon: <StopOutlined /> },
    10: { text: "En correccion", color: "#ffc069", icon: <SyncOutlined /> },
    11: { text: "Cancelación Solicitada", color: "#ffadd2", icon: <StopOutlined /> },
    12: { text: "En Queja", color: "#ffbb96", icon: <ExclamationCircleOutlined /> },
    "finished": { text: "Entregado", color: "#b7eb8f", icon: <CheckCircleOutlined /> },
    "canceled": { text: "Rechazado", color: "#ff7875", icon: <CloseCircleOutlined /> },
    "quote": { text: "En Cotización", color: "#FFD700", icon: <CheckCircleOutlined /> },
    "build": { text: "Construcción", color: "#69c0ff", icon: <ToolOutlined /> },
    "togo": { text: "En Camino", color: "#95de64", icon: <CarOutlined /> },
    "review": { text: "En Revisión", color: "#d3adf7", icon: <SolutionOutlined /> },
    "aprove": { text: "Aprobado", color: "#b7eb8f", icon: <CheckCircleOutlined /> },
};

const categoryMap = {
  1: { name: "MRO", color: "#ffccc7" },
  2: { name: "Empaque", color: "#fff1b8" },
  3: { name: "MRP", color: "#ffccc7" },
  4: { name: "Servicios", color: "#d6e4ff" },
  5: { name: "Maquinado", color: "#b5f5ec" },
  6: { name: "Tecnologia", color: "#d9f7be" },
};

const categoryFilterOptions = [
  { text: "MRP", value: "MRP" },
  { text: "MRO", value: "MRO" },
  { text: "Maquinado", value: "Maquinado" },
  { text: "Empaque", value: "Empaque" },
  { text: "Servicios", value: "Servicios" },
  { text: "Tecnologia", value: "Tecnologia" },
];

const formatPrice = (price) => {
    if (price === null || price === undefined) {
        return "N/A";
    }
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
    }).format(price);
};

export default function ServicesTable({
  openOrders = [],
  loading,
  pagination,
  onTableChange,
  searchText = "",
  onSearchChange,
}) {
  const navigate = useNavigate();

  const [activeSort, setActiveSort] = useState({ key: null, order: null });

  const filteredOrders = useMemo(() => openOrders, [openOrders]);

  const getSortValue = (record, key) => {
    if (key === "updated_at") return moment(record?.updated_at).valueOf();
    if (key === "created_at") return moment(record?.created_at).valueOf();
    if (key === "epno_price") return record?.epno_price ?? -1;
    return 0;
  };

  const sortedOrders = useMemo(() => {
    if (!activeSort.key || !activeSort.order) return filteredOrders;

    return [...filteredOrders].sort((a, b) => {
      const left = getSortValue(a, activeSort.key);
      const right = getSortValue(b, activeSort.key);

      if (activeSort.order === "ascend") {
        return left - right;
      }
      return right - left;
    });
  }, [filteredOrders, activeSort]);

  const getSortMenu = (columnKey) => {
    const isPriceColumn = columnKey === "epno_price";

    return {
    items: [
      { key: "none", label: "Sin ordenar" },
      {
        key: "ascend",
        label: isPriceColumn ? "Menor" : "Más antiguo",
      },
      {
        key: "descend",
        label: isPriceColumn ? "Mayor" : "Más reciente",
      },
    ],
    selectable: true,
    selectedKeys:
      activeSort.key === columnKey && activeSort.order
        ? [activeSort.order]
        : ["none"],
    onClick: ({ key }) => {
      if (key === "none") {
        setActiveSort({ key: null, order: null });
        return;
      }
      setActiveSort({ key: columnKey, order: key });
    },
    };
  };

  const renderSortableTitle = (label, columnKey) => (
    <div className="table-sort-title">
      <span>{label}</span>
      <Dropdown menu={getSortMenu(columnKey)} trigger={["click"]}>
        <span
          role="button"
          tabIndex={0}
          className={`ant-table-filter-trigger table-sort-trigger ${
            activeSort.key === columnKey ? "is-active" : ""
          }`}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          aria-label={`Ordenar ${label}`}
        >
          <FilterOutlined />
        </span>
      </Dropdown>
    </div>
  );

  const columns = [
    {
      title: "ID Orden",
      dataIndex: "orderId",
      key: "orderId",
      width: 120,
      render: (text, record) =>
        record.orderId ? (
          <Link to={`/orders/details/new/${record.orderId}`}>{text}</Link>
        ) : (
          <span>N/A</span>
        ),
    },
    {
      title: "ID Servicio",
      dataIndex: "serviceId",
      key: "serviceId",
      width: 120,
    },
    {
      title: "Cliente",
      dataIndex: "client_bussiness",
      key: "client_bussiness",
      width: 190,
      render: (text, record) => {
        return (
            <>
                <Avatar size="small" className="table-avatar" icon={<UserOutlined />} />
                {text || 'N/A'}
            </>
        );
      }
    },
    {
        title: "Título",
        dataIndex: "title",
        key: "title",
      width: 260,
        responsive: ["md"],
        render: (text) => text || 'N/A'
    },
    {
        title: "Descripción",
        dataIndex: "description",
        key: "description",
      width: 320,
        responsive: ["lg"],
        render: (text) => text || 'N/A'
    },
    {
      title: "Categoría",
      dataIndex: "cat",
      key: "cat",
      width: 170,
      filters: categoryFilterOptions,
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? "#ffffff" : "#d6e4ff" }} />
      ),
      onFilter: (value, record) => {
        const categoryName = categoryMap[record?.cat]?.name;
        return categoryName === value;
      },
      render: (catId) => {
        const category = categoryMap[catId];
        return category ? (
          <Tag color={category.color} style={{ color: '#000', borderRadius: '12px', padding: '2px 8px' }}>{category.name}</Tag>
        ) : (
          <Tag>Desconocida</Tag>
        );
      },
    },
    {
        title: "Estatus",
        dataIndex: "status",
        key: "status",
      width: 190,
        render: (statusId) => {
          const status = statusDisplayMap[statusId] || { text: 'Desconocido', color: '#d9d9d9', icon: <QuestionCircleOutlined /> };
          return (
            <Tag icon={status.icon} color={status.color} style={{ color: '#000', borderRadius: '12px', padding: '2px 8px' }}>
                {status.text}
            </Tag>
          );
        },
    },
    {
      title: renderSortableTitle("Actualización", "updated_at"),
      dataIndex: "updated_at",
      key: "updated_at",
      width: 150,
      render: (t) => moment(t).fromNow(),
    },
    {
        title: renderSortableTitle("Creación", "created_at"),
        dataIndex: "created_at",
        key: "created_at",
        width: 130,
        render: (t) => moment(t).format("DD/MM/YY"),
    },
    {
        title: renderSortableTitle("Valor", "epno_price"),
        dataIndex: "epno_price",
        key: "epno_price",
      width: 120,
        render: (price) => {
            if (price === null || price === undefined) {
                return <Tag color="red">N/A</Tag>;
            }
            const tagColor = price > 0 ? 'green' : 'red';
            return <Tag color={tagColor}>{formatPrice(price)}</Tag>;
        },
    },
  ];

  const effectivePagination = useMemo(() => {
    if (!pagination || typeof pagination !== "object") {
      return pagination;
    }

    const pageSize = pagination.pageSize || 15;
    const total = Number(pagination.total ?? sortedOrders.length);
    const current = pagination.current || 1;

    return {
      ...pagination,
      total,
      current,
      pageSize,
      showTotal: (totalItems, range) => {
        if (!totalItems) return "Mostrando 0 servicios";
        return `Mostrando ${range[0]}-${range[1]} de ${totalItems} servicios`;
      },
    };
  }, [pagination, sortedOrders.length]);

  const handleSearchInputChange = (e) => {
    const nextValue = e.target.value;

    if (onSearchChange) {
      onSearchChange(nextValue);
    }

    if (
      onTableChange &&
      pagination &&
      typeof pagination === "object" &&
      (pagination.current || 1) !== 1
    ) {
      onTableChange({
        ...pagination,
        current: 1,
      });
    }
  };

  return (
    <div className="styled-table table-container">
      <Input
        placeholder="Buscar por ID, titulo, descripción, cliente, etc ..."
        value={searchText}
        onChange={handleSearchInputChange}
        style={{ marginBottom: 16 }}
        prefix={<SearchOutlined />}
      />
      {loading ? (
        <Skeleton active paragraph={{ rows: 8 }} title={false} />
      ) : (
        <Table
          columns={columns}
          dataSource={sortedOrders}
          rowKey="serviceId"
          scroll={{ x: 1770 }}
          pagination={effectivePagination}
          onChange={(newPagination, filters, sorter, extra) => {
            if (onTableChange) {
              onTableChange(newPagination, filters, sorter, extra);
            }
          }}
          rowClassName={(record) =>
            record?.orderId ? "orders-clickable-row" : ""
          }
          onRow={(record) => ({
            onClick: () => {
              if (!record?.orderId) return;
              navigate(`/orders/details/new/${record.orderId}`);
            },
          })}
        />
      )}
    </div>
  );
}
